// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Employee, State } from "@utils/types";
import { UIMessages } from "../../config/constant";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";
import { SnackMessage } from "../../config/constant";
import axios, { HttpStatusCode } from "axios";

interface EmployeeState {
  employees: EmployeeInfo[];
  state: State;
  employeesState: State;
  stateMessage: string | null;
  errorMessage: string | null;
  employee: EmployeeInfo | null;
  [key: string]: any;
}

const initialState: EmployeeState = {
  employees: [],
  state: State.idle,
  employeesState: State.idle,
  stateMessage: "",
  errorMessage: "",
  employee: null,
};

export interface EmployeeInfo {
  employeeId: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  workEmail: string;
  department: string;
  team: string;
  jobRole: string;
  employeeThumbnail: string;
  location: string;
}

export const fetchEmployeeInfo = createAsyncThunk(
  "employee/fetchEmployeeInfo",
  async (employeeWorkEmail: string, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    return new Promise<EmployeeInfo>((resolve, reject) => {
      APIService.getInstance()
        .get(
          `${AppConfig.serviceUrls.getEmployeeInfo}?employeeWorkEmail=${employeeWorkEmail}`,
          {
            cancelToken: newCancelTokenSource.token,
          }
        )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return rejectWithValue("Request canceled");
          }
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === HttpStatusCode.InternalServerError
                  ? SnackMessage.error.fetchEmployeeInfoMessage
                  : String(error.response.data.message),
              type: "error",
            })
          );
          reject(error.response.data.message);
        });
    });
  }
);

export const fetchEmployees = createAsyncThunk(
  "employee/fetchEmployees",
  async (_, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    return new Promise<EmployeeInfo[]>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.getEmployees}`, {
          cancelToken: newCancelTokenSource.token,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return rejectWithValue("Request canceled");
          }
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === HttpStatusCode.InternalServerError
                  ? SnackMessage.error.fetchEmployeesMessage
                  : String(error.response.data.message),
              type: "error",
            })
          );
          reject(error.response.data.message);
        });
    });
  }
);

const EmployeeSlice = createSlice({
  name: "getEmployeeInfo",
  initialState,
  reducers: {
    clearEmployee: (state) => {
      state.employee = null;
      state.stateMessage = null;
      state.errorMessage = null;
      state.state = State.idle;
    },
    setSelectedEmployee: (state, action: PayloadAction<EmployeeInfo>) => {
      state.employee = action.payload;
      state.state = State.success;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeInfo.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = UIMessages.loading.loadingEmployees;
      })
      .addCase(
        fetchEmployeeInfo.fulfilled,
        (state, action: PayloadAction<EmployeeInfo>) => {
          state.state = State.success;
          state.stateMessage = UIMessages.success.employeeFetch;
          state.employee = action.payload;
        }
      )
      .addCase(fetchEmployeeInfo.rejected, (state, action) => {
        state.state = State.failed;
        state.stateMessage = UIMessages.error.employees;
        state.errorMessage = action.payload as string;
      })
      .addCase(fetchEmployees.pending, (state) => {
        state.employeesState = State.loading;
        state.stateMessage = UIMessages.loading.loadingEmployees;
      })
      .addCase(
        fetchEmployees.fulfilled,
        (state, action: PayloadAction<EmployeeInfo[]>) => {
          state.employeesState = State.success;
          state.stateMessage = UIMessages.success.employeeFetch;
          state.employees = action.payload;
        }
      )
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.employeesState = State.failed;
        state.stateMessage = UIMessages.error.employees;
        state.errorMessage = action.payload as string;
      });
  },
});

export const { clearEmployee, setSelectedEmployee } = EmployeeSlice.actions;

export default EmployeeSlice.reducer;
